// Tutorial Videos Subpage - Shows all tutorial videos with sidebar navigation and grid layout
// Follows the Section, Component & Manager System architecture

import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { LearnManager } from '../../managers/learn-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { LearnPageHeader, LikeButton, LearnLoading } from '../../components';
import '../../LearnShared.css';
import './TutorialVideosLayout.css';
import './TutorialVideosSidebar.css';
import './TutorialVideosContent.css';
import './TutorialVideosGrid.css';
import './TutorialVideoSearch.css';
import { TutorialVideosLayout } from './TutorialVideosLayout';
import { TutorialVideosSidebar } from './TutorialVideosSidebar';
import { TutorialVideosContent } from './TutorialVideosContent';
import { TutorialVideosGrid } from './TutorialVideosGrid';
import { TutorialVideoSearch } from './TutorialVideoSearch';
import { extractVideoId, getEmbedUrl } from '../../utils';

export function TutorialVideosSection() {
  const { manager, isInitialized } = useManager(LearnManager);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get the first category ID as default, or use URL param
  const firstCategoryId = manager.categories.length > 0 ? manager.categories[0]?.id : null;
  const currentCategory = searchParams.get('tab') || firstCategoryId || 'all';
  const videoId = searchParams.get('video');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Individual video data state (for userLiked status)
  const [individualVideo, setIndividualVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  // Admin edit handler
  const handleAdminEdit = (videoId, categoryId) => {
    // Navigate to admin section with payload to auto-open edit modal
    navigate(`/learn/admin?tab=tutorial-videos&adminTab=${categoryId}`, {
      state: {
        action: 'editVideo',
        videoId: videoId,
        categoryId: categoryId,
        fromSection: 'tutorial-videos'
      }
    });
  };

  // Fetch individual video data when videoId is present
  React.useEffect(() => {
    if (videoId && manager && isInitialized) {
      const fetchIndividualVideo = async () => {
        setLoadingVideo(true);
        try {
          const videoData = await manager.getVideoWithLikeStatus(videoId);
          setIndividualVideo(videoData);
        } catch (error) {
          console.error('Failed to fetch individual video:', error);
          setIndividualVideo(null);
        } finally {
          setLoadingVideo(false);
        }
      };

      fetchIndividualVideo();
    } else {
      setIndividualVideo(null);
    }
  }, [videoId, manager, isInitialized]);

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--learn">
        <LearnLoading
          title="Loading Tutorial Videos"
          subtitle="Preparing your learning experience..."
        />
      </MainLayout>
    );
  }

  // Get videos filtered by category
  let filteredVideos = manager.getFilteredTutorialVideos(currentCategory);

  // Apply additional search filtering
  if (searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase().trim();

    if (searchLower.startsWith('#')) {
      // Tag search: #basic-actions searches for tag "basic-actions"
      const tagQuery = searchLower.slice(1);
      filteredVideos = filteredVideos.filter(video =>
        video.tags.some(tag => tag.toLowerCase().includes(tagQuery))
      );
    } else {
      // Title search: regular text searches video titles
      filteredVideos = filteredVideos.filter(video =>
        video.name.toLowerCase().includes(searchLower)
      );
    }
  }

  // Get category display info
  const getCategoryInfo = (categoryId) => {
    // Find the category in the real categories data
    const category = manager.categories.find(cat => cat.id === categoryId);

    if (category) {
      return {
        title: category.name,
        description: `Explore our ${category.name.toLowerCase()} tutorials and learn more about this topic.`
      };
    }

    // Fallback for when category is not found
    return { title: 'Unknown', description: 'Category description not available.' };
  };

  const categoryInfo = getCategoryInfo(currentCategory);

  // If videoId is present, render video detail view
  if (videoId) {
    // Use individual video data if available, otherwise fall back to cached data
    const video = individualVideo || manager.tutorialVideos.find(v => v.id === videoId);
    const categoryInfo = getCategoryInfo(currentCategory);

    // Show loading while fetching individual video data
    if (loadingVideo) {
      return (
        <MainLayout contentClassName="main-content--learn">
          <LearnLoading
            title="Loading Video"
            subtitle="Getting video details..."
          />
        </MainLayout>
      );
    }

    if (!video) {
      return (
        <MainLayout contentClassName="main-content--learn">
          <LearnPageHeader />
          <div className="learn-container">
            <div className="learn-empty-state">
              <i className="fa-solid fa-video-slash"></i>
              <h3>Video not found</h3>
              <p>The requested tutorial video could not be found.</p>
              <button
                className="learn-back-btn"
                onClick={() => navigate(`/learn/tutorial-videos?tab=${currentCategory}`)}
              >
                <i className="fa-solid fa-arrow-left"></i> Back to Tutorial Videos
              </button>
            </div>
          </div>
        </MainLayout>
      );
    }

    return (
      <MainLayout contentClassName="main-content--learn">
        {/* Fixed Learn Page Header */}
        <LearnPageHeader />

        <div className="learn-container learn-container--video-detail">
          {/* Breadcrumb Navigation - Left Aligned */}
          <div className="learn-breadcrumb learn-breadcrumb--video-detail">
            <button
              className="learn-breadcrumb-link"
              onClick={() => navigate('/learn')}
            >
              Learn
            </button>
            <i className="fa-solid fa-chevron-right learn-breadcrumb-separator"></i>
            <button
              className="learn-breadcrumb-link"
              onClick={() => navigate('/learn/tutorial-videos')}
            >
              Tutorial Videos
            </button>
            <i className="fa-solid fa-chevron-right learn-breadcrumb-separator"></i>
            <button
              className="learn-breadcrumb-link"
              onClick={() => navigate(`/learn/tutorial-videos?tab=${currentCategory}`)}
            >
              {categoryInfo.title}
            </button>
            <i className="fa-solid fa-chevron-right learn-breadcrumb-separator"></i>
            <span className="learn-breadcrumb-current">
              {video.name}
              {user?.role === 'admin' && (
                <button
                  className="admin-edit-btn"
                  onClick={() => handleAdminEdit(video.id, currentCategory)}
                  title="Edit video in admin panel"
                >
                  <i className="fa-solid fa-edit"></i>
                  Admin Edit
                </button>
              )}
            </span>
          </div>

          {/* Video Content Layout - Left/Right Split */}
          <div className="learn-video-content-layout">
            {/* Left Side - Video Information */}
            <div className="learn-video-info-section">
              <div className="learn-video-header">
                <h1 className="learn-video-detail-title">{video.name}</h1>
                {(video.date || video.duration) && (
                  <div className="learn-video-stats">
                    {video.date && (
                      <span className="learn-stat">
                        <i className="fa-solid fa-calendar"></i> {new Date(video.date).toLocaleDateString()}
                      </span>
                    )}
                    {video.duration && (
                      <span className="learn-stat">
                        <i className="fa-solid fa-clock"></i> {video.duration}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="learn-video-description">
                <p>{video.description}</p>
              </div>

              <div className="learn-video-tags">
                <div className="learn-tag-list">
                  {video.tags && video.tags.length > 0 ? video.tags.map((tag, index) => (
                    <span key={index} className="learn-tag">{tag}</span>
                  )) : <span className="learn-no-tags">No tags</span>}
                </div>
              </div>

              {/* Like Button */}
              <div className="learn-video-actions">
                <LikeButton
                  videoId={video.id}
                  initialLikes={video.likes || 0}
                  initialLiked={video.userLiked || false}
                  manager={manager}
                />
              </div>
            </div>

            {/* Right Side - Video Player */}
            <div className="learn-video-player-section">
              <div className="learn-video-player">
                {video.videoUrl ? (() => {
                  const videoInfo = extractVideoId(video.videoUrl);
                  const embedUrl = getEmbedUrl(videoInfo);

                  if (embedUrl) {
                    return (
                      <div className="learn-video-wrapper">
                        <iframe
                          src={embedUrl}
                          title={video.name || 'Tutorial Video'}
                          className="learn-video-iframe"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div className="learn-video-error">
                        <i className="fa-solid fa-exclamation-triangle"></i>
                        <span>Unable to load video. Please check the video URL.</span>
                      </div>
                    );
                  }
                })() : (
                  <div className="learn-video-placeholder">
                    <div className="learn-video-placeholder-content">
                      <i className="fa-solid fa-video"></i>
                      <span>No video URL available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Referenced Videos by Type */}
          {/* Previous Videos */}
          {(() => {
            const previousVideos = video.referencedVideos
              ?.filter(ref => ref.type === 'previous')
              .map(ref => manager.tutorialVideos.find(v => v.id === ref.videoId))
              .filter(Boolean) || [];

            return previousVideos.length > 0 && (
              <div className="learn-related-section">
                <h3>Previous Videos</h3>
                <TutorialVideosGrid
                  videos={previousVideos}
                  currentCategory={currentCategory}
                  onVideoClick={(relatedVideoId) => navigate(`/learn/tutorial-videos?video=${relatedVideoId}&tab=${currentCategory}`)}
                />
              </div>
            );
          })()}

          {/* Next Videos */}
          {(() => {
            const nextVideos = video.referencedVideos
              ?.filter(ref => ref.type === 'next')
              .map(ref => manager.tutorialVideos.find(v => v.id === ref.videoId))
              .filter(Boolean) || [];

            return nextVideos.length > 0 && (
              <div className="learn-related-section">
                <h3>Next Videos</h3>
                <TutorialVideosGrid
                  videos={nextVideos}
                  currentCategory={currentCategory}
                  onVideoClick={(relatedVideoId) => navigate(`/learn/tutorial-videos?video=${relatedVideoId}&tab=${currentCategory}`)}
                />
              </div>
            );
          })()}

          {/* Related Videos */}
          {(() => {
            const relatedVideos = video.referencedVideos
              ?.filter(ref => ref.type === 'related')
              .map(ref => manager.tutorialVideos.find(v => v.id === ref.videoId))
              .filter(Boolean) || [];

            return relatedVideos.length > 0 && (
              <div className="learn-related-section">
                <h3>Related Videos</h3>
                <TutorialVideosGrid
                  videos={relatedVideos}
                  currentCategory={currentCategory}
                  onVideoClick={(relatedVideoId) => navigate(`/learn/tutorial-videos?video=${relatedVideoId}&tab=${currentCategory}`)}
                />
              </div>
            );
          })()}
        </div>
      </MainLayout>
    );
  }

  // Default: render video list view
  return (
    <MainLayout contentClassName="main-content--learn">
      {/* Fixed Learn Page Header */}
      <LearnPageHeader />

      <TutorialVideosLayout
        sidebar={<TutorialVideosSidebar categories={manager.categories} videos={manager.tutorialVideos} />}
        content={
          <TutorialVideosContent>
            {/* Category Header */}
            <div className="tutorial-videos-header">
              <div className="category-title-section">
                <div className="category-title-left">
                  <h2 className="category-title">{categoryInfo.title}</h2>
                  <span className="category-count-badge">{filteredVideos.length}</span>
                </div>
                <TutorialVideoSearch
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  placeholder={`Search ${categoryInfo.title.toLowerCase()} videos...`}
                />
              </div>
              <p className="category-description">{categoryInfo.description}</p>
            </div>

            {filteredVideos.length === 0 ? (
              <div className="learn-empty-state">
                <i className="fa-solid fa-video-slash"></i>
                <h3>No tutorial videos found</h3>
                <p>Try adjusting your search terms or check back later for new content.</p>
              </div>
            ) : (
              <TutorialVideosGrid
                videos={filteredVideos}
                currentCategory={currentCategory}
                onVideoClick={(videoId) => navigate(`/learn/tutorial-videos?video=${videoId}&tab=${currentCategory}`)}
              />
            )}
          </TutorialVideosContent>
        }
      />
    </MainLayout>
  );
}